import React, { useRef, useState } from 'react';
import {
  Modal,
  Stack,
  Text,
  Button,
  Group,
  Loader,
  TextInput,
  TagsInput,
  Radio,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCreateFirstMap, useFirstMapDraft } from '../../hooks/useCompetencyApi';
import { useGrades } from '../../hooks/useReferenceApi';
import { getErrorMessage } from '../../utils/api';
import { DraftRows, type DraftRow } from './DraftRows';

// Готовые шкалы: человек выбирает привычную, а не придумывает «грейды» с нуля. Уровни — в локалях.
const SCALE_KEYS = ['ranks', 'specialist', 'it'];

interface FirstMapWizardProps {
  opened: boolean;
  onClose: () => void;
}

/**
 * Мастер «Первая карта»: профессия и шкала уровней → ИИ предлагает направления и компетенции →
 * человек правит черновик → карта создаётся вместе с уровнями и направлениями. Для компании,
 * где ещё ничего нет: ни грейдов, ни свимлейнов, ни карт.
 */
export const FirstMapWizard: React.FC<FirstMapWizardProps> = ({ opened, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: companyGrades = [] } = useGrades();
  const draft = useFirstMapDraft();
  const create = useCreateFirstMap();

  // Уровни компании, если они уже есть, — первая шкала и выбор по умолчанию.
  const own = [...(companyGrades as { title: string; level?: number }[])]
    .sort((a, b) => (a.level ?? Infinity) - (b.level ?? Infinity))
    .map((g) => g.title);
  const scales: Record<string, string[]> = {
    ...(own.length ? { company: own } : {}),
    ...Object.fromEntries(
      SCALE_KEYS.map((key) => [
        key,
        t(`competencyMaps.first.scaleLevels.${key}`, { returnObjects: true }) as string[],
      ]),
    ),
  };

  const [profession, setProfession] = useState('');
  const [pickedScale, setPickedScale] = useState<string | null>(null);
  const [customLevels, setCustomLevels] = useState<string[]>([]);
  const [swimlanes, setSwimlanes] = useState<string[]>([]);
  const [isSwimlanesOpen, setIsSwimlanesOpen] = useState(false);
  const scale = pickedScale ?? (own.length ? 'company' : null);
  const grades = scale === 'custom' ? customLevels : scale ? (scales[scale] ?? []) : [];
  const [rows, setRows] = useState<DraftRow[] | null>(null);
  const [lanes, setLanes] = useState<string[]>([]);
  // Окно закрыли, пока ИИ думал, — ответ уже не нужен.
  const runRef = useRef(0);

  const handleGenerate = async () => {
    const run = runRef.current;
    const data = await draft
      .mutateAsync({ profession: profession.trim(), grades, swimlanes })
      .catch(() => null);
    if (!data || run !== runRef.current) return;
    setLanes(data.swimlanes);
    // В строках редактора уровень и направление — названия: в базе их ещё нет.
    setRows(
      data.competencies.map((c) => ({
        title: c.title,
        description: c.description,
        gradeId: c.grade,
        swimlaneId: c.swimlane,
        isKept: true,
      })),
    );
  };

  const handleClose = () => {
    runRef.current++;
    setRows(null);
    draft.reset();
    create.reset();
    onClose();
  };

  const kept = (rows ?? []).filter((r) => r.isKept && r.title.trim() && r.description.trim());

  const handleCreate = async () => {
    const result = await create
      .mutateAsync({
        profession: profession.trim(),
        grades,
        competencies: kept.map((r) => ({
          title: r.title.trim(),
          description: r.description.trim(),
          grade: r.gradeId,
          swimlane: r.swimlaneId,
        })),
      })
      .catch(() => null);
    if (!result) return;
    handleClose();
    navigate(`/competency-maps/${result.id}`);
  };

  const options = (list: string[]) => list.map((v) => ({ value: v, label: v }));
  const error = draft.error ?? create.error;
  const canGenerate = profession.trim().length >= 2 && grades.length > 0;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t('competencyMaps.first.title')}
      size={rows ? 'xl' : 'md'}
    >
      <Stack gap="md">
        {!rows && (
          <>
            <Text size="sm" c="dimmed">
              {t('competencyMaps.first.intro')}
            </Text>
            <TextInput
              label={t('competencyMaps.first.profession')}
              placeholder={t('competencyMaps.first.professionPlaceholder')}
              value={profession}
              maxLength={100}
              onChange={(e) => setProfession(e.currentTarget.value)}
              data-autofocus
            />
            <Radio.Group
              label={t('competencyMaps.first.scale')}
              value={scale}
              onChange={setPickedScale}
            >
              <Stack gap="xs" mt={6}>
                {[...Object.keys(scales), 'custom'].map((key) => (
                  <Radio.Card key={key} value={key} p="sm" radius="md">
                    <Group wrap="nowrap" gap="sm" align="flex-start">
                      <Radio.Indicator />
                      <div>
                        <Text size="sm" fw={500}>
                          {t(`competencyMaps.first.scales.${key}`)}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {key === 'custom'
                            ? t('competencyMaps.first.customHint')
                            : scales[key].join(', ')}
                        </Text>
                      </div>
                    </Group>
                  </Radio.Card>
                ))}
              </Stack>
            </Radio.Group>
            {scale === 'custom' && (
              <TagsInput
                label={t('competencyMaps.first.levels')}
                description={t('competencyMaps.first.levelsHint')}
                value={customLevels}
                onChange={setCustomLevels}
                maxTags={10}
              />
            )}
            {isSwimlanesOpen ? (
              <TagsInput
                label={t('competencyMaps.first.swimlanes')}
                description={t('competencyMaps.first.swimlanesHint')}
                value={swimlanes}
                onChange={setSwimlanes}
                maxTags={8}
              />
            ) : (
              <Group>
                <Button variant="subtle" size="compact-sm" onClick={() => setIsSwimlanesOpen(true)}>
                  {t('competencyMaps.first.swimlanesOpen')}
                </Button>
              </Group>
            )}
            {draft.isPending ? (
              <Group gap="sm">
                <Loader size="sm" />
                <Text size="sm">{t('competencyMaps.draft.waiting')}</Text>
              </Group>
            ) : (
              <Group justify="flex-end">
                <Button onClick={handleGenerate} disabled={!canGenerate}>
                  {t('competencyMaps.draft.generate')}
                </Button>
              </Group>
            )}
          </>
        )}

        {rows && rows.length === 0 && <Text size="sm">{t('competencyMaps.draft.empty')}</Text>}
        {rows && rows.length > 0 && (
          <DraftRows
            rows={rows}
            onChange={setRows}
            grades={options(grades)}
            swimlanes={options(lanes)}
          />
        )}

        {error && (
          <Text size="sm" c="red">
            {getErrorMessage(error)}
          </Text>
        )}

        {rows && (
          <Group justify="space-between">
            <Group gap="xs">
              <Button variant="subtle" onClick={() => setRows(null)}>
                {t('common.back')}
              </Button>
              <Button variant="subtle" onClick={handleGenerate} loading={draft.isPending}>
                {t('competencyMaps.draft.regenerate')}
              </Button>
            </Group>
            <Button onClick={handleCreate} disabled={!kept.length} loading={create.isPending}>
              {t('competencyMaps.first.create', { count: kept.length })}
            </Button>
          </Group>
        )}
      </Stack>
    </Modal>
  );
};
